import React, { useState, useEffect } from 'react';
import { baseUrl } from '../../shared';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

// ── Mobile calibration helpers ────────────────────────────────────────────────

/**
 * Linear interpolation of NM offset from correction data at the given PSI.
 * Returns 0 if no offsets or PSI is out of range (no extrapolation).
 */
export function applyOffset(psi, offsets) {
  if (!offsets || offsets.length === 0) return 0;
  const sorted = [...offsets].sort((a, b) => a.psi - b.psi);
  if (psi <= sorted[0].psi) return sorted[0].offsetNm;
  if (psi >= sorted[sorted.length - 1].psi) return sorted[sorted.length - 1].offsetNm;
  for (let i = 0; i < sorted.length - 1; i++) {
    if (psi >= sorted[i].psi && psi <= sorted[i + 1].psi) {
      const t = (psi - sorted[i].psi) / (sorted[i + 1].psi - sorted[i].psi);
      return sorted[i].offsetNm + t * (sorted[i + 1].offsetNm - sorted[i].offsetNm);
    }
  }
  return 0;
}

/**
 * Picker that shows only portable transducers with loaded correction data.
 * Props: value (equipmentId or null), onChange(id, transducerObj), readOnly
 */
export function MobileTransducerPicker({ value, onChange, readOnly }) {
  const [transducers, setTransducers] = useState([]);

  useEffect(() => {
    fetch(baseUrl + 'workshop/settings/equipment', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        const portable = (Array.isArray(d) ? d : [])
          .filter(e => e.isActive && e.isPortableTransducer);
        setTransducers(portable);
      });
  }, []);

  const selected = transducers.find(t => t.id === value);
  const hasOffsets = selected && selected.latestCorrectionData && selected.latestCorrectionData.length > 0;

  if (readOnly) {
    return selected
      ? <Typography variant="body2">{selected.name}{selected.serialNo ? ` S/N ${selected.serialNo}` : ''}</Typography>
      : <Typography variant="body2" color="text.secondary">—</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FormControl fullWidth size="small">
        <InputLabel>Portable Transducer</InputLabel>
        <Select
          value={value || ''}
          label="Portable Transducer"
          onChange={e => {
            const id = e.target.value;
            const t = transducers.find(x => x.id === id) || null;
            onChange(id || null, t);
          }}
        >
          <MenuItem value=""><em>Select transducer…</em></MenuItem>
          {transducers.map(t => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}{t.serialNo ? ` S/N ${t.serialNo}` : ''}
              {t.latestCorrectionData && t.latestCorrectionData.length > 0
                ? ` (${t.latestCorrectionData.length} offsets)`
                : ' — no offsets'
              }
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {value && !hasOffsets && (
        <Alert severity="warning" sx={{ py: 0, fontSize: 12 }}>
          This transducer has no correction offsets recorded. Add a calibration record with offsets in Workshop Settings.
        </Alert>
      )}
      {value && hasOffsets && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip icon={<PhoneAndroidIcon />} label="Mobile mode active" size="small" color="info" />
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Offsets loaded — enter raw transducer readings; corrected values calculated automatically
          </Typography>
        </Box>
      )}
      {transducers.length === 0 && (
        <Alert severity="info" sx={{ py: 0, fontSize: 12 }}>
          No portable transducers registered. Mark equipment as portable transducer in Workshop Settings.
        </Alert>
      )}
    </Box>
  );
}

export function EquipmentSelector({ selected, onChange, readOnly }) {
  const [equipment, setEquipment] = useState([]);

  useEffect(() => {
    fetch(baseUrl + 'workshop/settings/equipment', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setEquipment(Array.isArray(d) ? d.filter(e => e.isActive) : []));
  }, []);

  const toggle = (item) => {
    const alreadyIn = selected.some(s => s.id === item.id);
    if (alreadyIn) {
      onChange(selected.filter(s => s.id !== item.id));
    } else {
      const snapshot = {
        id: item.id,
        name: item.name,
        model: item.model || '',
        serialNo: item.serialNo || '',
        certNo: item.latestCertNo || '',
        certDate: item.latestCertDate || '',
        certDueDate: item.latestCertDueDate || '',
        traceability: item.latestTraceability || '',
        uncertaintyPct: item.latestUncertaintyPct,
        maxErrorPct: item.latestMaxErrorPct,
      };
      onChange([...selected, snapshot]);
    }
  };

  if (readOnly) {
    if (!selected || selected.length === 0) return <Typography variant="body2" color="text.secondary">No equipment recorded.</Typography>;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {selected.map(e => (
          <Typography key={e.id} variant="body2">
            {e.name}{e.model ? ` ${e.model}` : ''}{e.serialNo ? ` S/N ${e.serialNo}` : ''}
            {e.certNo ? ` — Cal Cert: ${e.certNo}` : ''}
            {e.certDate ? ` (${dayjs(e.certDate).format('DD/MM/YYYY')})` : ''}
          </Typography>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {equipment.map(item => {
        const isChecked = selected.some(s => s.id === item.id);
        const isOverdue = item.latestCertDueDate && dayjs(item.latestCertDueDate).isBefore(dayjs(), 'day');
        return (
          <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <FormControlLabel
              control={<Checkbox checked={isChecked} onChange={() => toggle(item)} size="small" disabled={isOverdue} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2">
                    {item.name}{item.model ? ` ${item.model}` : ''}{item.serialNo ? ` (S/N ${item.serialNo})` : ''}
                  </Typography>
                  {item.latestCertNo && (
                    <Typography variant="caption" color="text.secondary">
                      — Cert: {item.latestCertNo}
                      {item.latestCertDueDate ? ` | Due ${dayjs(item.latestCertDueDate).format('DD/MM/YYYY')}` : ''}
                    </Typography>
                  )}
                  {isOverdue && (
                    <Tooltip title="Calibration overdue — cannot be selected">
                      <WarningAmberIcon fontSize="small" color="error" />
                    </Tooltip>
                  )}
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Box>
        );
      })}
      {equipment.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No active test equipment registered. Add equipment in Workshop Settings.
        </Typography>
      )}
    </Box>
  );
}
